import { useToast } from '@/components/ui/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CohortByIdQuery,
  CohortByIdQueryVariables,
  FETCH_COHORT_BY_ID,
  FETCH_YEAR_GROUPS,
  FetchReportingToolsCohortSummaryQuery,
  FetchYearGroupsQuery,
  FetchYearGroupsQueryVariables,
  UPDATE_COHORT,
  UpdateCohortMutation,
  UpdateCohortMutationVariables,
} from '@edwin-group/cj-graphql';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';

export type CohortSummaryListRow = NonNullable<
  NonNullable<
    NonNullable<FetchReportingToolsCohortSummaryQuery['fetchReportingToolsCohortSummary']>['cohortSummary']
  >[number]
>;

const getRowCohortDocumentId = (cohortSummary: CohortSummaryListRow): string | null =>
  cohortSummary?.documentId ?? null;

const cohortEditSchema = z.object({
  cohortName: z.string().trim().min(1, 'Cohort name is required.'),
  yearGroupDocumentId: z.string().min(1, 'Year group is required.'),
  classNames: z
    .array(
      z.object({
        name: z.string().trim().min(1, 'Class name is required.'),
      }),
    )
    .min(1, 'At least one class is required.'),
});

type CohortEditFormData = z.infer<typeof cohortEditSchema>;

const defaultValues: CohortEditFormData = {
  cohortName: '',
  yearGroupDocumentId: '',
  classNames: [{ name: '' }],
};

const mapCohortToFormData = (cohort: NonNullable<CohortByIdQuery['cohort']>): CohortEditFormData => ({
  cohortName: cohort.name || '',
  yearGroupDocumentId: cohort.yearGroup?.documentId || '',
  classNames:
    cohort.classes?.length && cohort.classes.some((cohortClass) => cohortClass?.name)
      ? cohort.classes
          .map((cohortClass) => cohortClass?.name?.trim())
          .filter((name): name is string => Boolean(name))
          .map((name) => ({ name }))
      : [{ name: '' }],
});

export const useCohortEdit = () => {
  const { toast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCohortDocumentId, setSelectedCohortDocumentId] = useState<string | null>(null);
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CohortEditFormData>({
    resolver: zodResolver(cohortEditSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'classNames',
  });

  const { data: yearGroupsResult } = useQuery<FetchYearGroupsQuery, FetchYearGroupsQueryVariables>(FETCH_YEAR_GROUPS, {
    fetchPolicy: 'cache-first',
  });

  const [fetchCohortById, { data: cohortByIdResult, loading: loadingCohortDetails }] = useLazyQuery<
    CohortByIdQuery,
    CohortByIdQueryVariables
  >(FETCH_COHORT_BY_ID, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateCohort, { loading: loadingUpdateCohort }] = useMutation<
    UpdateCohortMutation,
    UpdateCohortMutationVariables
  >(UPDATE_COHORT, {
    refetchQueries: 'all',
    awaitRefetchQueries: true,
  });
  const selectedCohort = cohortByIdResult?.cohort;

  useEffect(() => {
    if (!isEditModalOpen || !selectedCohort) return;
    reset(mapCohortToFormData(selectedCohort));
  }, [isEditModalOpen, selectedCohort, reset]);

  const yearGroupOptions = useMemo(
    () =>
      yearGroupsResult?.yearGroups?.filter((yearGroup): yearGroup is NonNullable<typeof yearGroup> =>
        Boolean(yearGroup?.documentId),
      ) || [],
    [yearGroupsResult?.yearGroups],
  );

  const handleOpenEditModal = async (cohortSummary: CohortSummaryListRow) => {
    const cohortDocumentId = getRowCohortDocumentId(cohortSummary);
    if (!cohortDocumentId) {
      toast({
        title: 'Error',
        description: 'Unable to edit cohort because cohort id is missing in Cohort Summary data.',
        variant: 'destructive',
      });
      return;
    }

    reset(defaultValues);
    setSelectedCohortDocumentId(cohortDocumentId);
    setIsEditModalOpen(true);
    const cohortResult = await fetchCohortById({ variables: { documentId: cohortDocumentId } });
    const fetchedCohort = cohortResult.data?.cohort;
    if (fetchedCohort) {
      reset(mapCohortToFormData(fetchedCohort));
    }
  };

  const handleAddClass = () => {
    append({ name: '' });
  };

  const handleRemoveClass = (index: number) => {
    if (fields.length === 1) return;
    remove(index);
  };

  const handleCloseEditModal = (isOpen: boolean) => {
    setIsEditModalOpen(isOpen);
    if (!isOpen) {
      setSelectedCohortDocumentId(null);
      reset(defaultValues);
    }
  };

  const onSubmit = async (values: CohortEditFormData) => {
    if (!selectedCohortDocumentId || !selectedCohort) return;

    const cleanedClasses = values.classNames.map(({ name }) => name.trim()).filter(Boolean);

    const cohortWithSchool = selectedCohort as typeof selectedCohort & {
      school?: { documentId?: string | null } | null;
    };

    const dataPayload = {
      name: values.cohortName.trim(),
      yearGroup: values.yearGroupDocumentId,
      classes: cleanedClasses.map((name) => ({ name })),
      ...(cohortWithSchool.school?.documentId ? { school: cohortWithSchool.school.documentId } : {}),
      ...(selectedCohort.schoolYear ? { schoolYear: selectedCohort.schoolYear } : {}),
      ...(selectedCohort.keyStage?.documentId ? { keyStage: selectedCohort.keyStage.documentId } : {}),
    };

    try {
      await updateCohort({
        variables: {
          documentId: selectedCohortDocumentId,
          data: dataPayload,
        },
      });

      toast({
        title: 'Success',
        description: 'Cohort updated successfully',
      });
      handleCloseEditModal(false);
    } catch (caught: unknown) {
      const errMessage = caught instanceof globalThis.Error ? caught.message : 'Unknown error';
      toast({
        title: 'Error',
        description: `Error updating cohort: ${errMessage}`,
        variant: 'destructive',
      });
    }
  };

  const handleSave = handleSubmit(onSubmit);

  return {
    isEditModalOpen,
    setIsEditModalOpen: handleCloseEditModal,
    loadingCohortDetails,
    loadingUpdateCohort,
    control,
    register,
    errors,
    fields,
    yearGroupOptions,
    handleOpenEditModal,
    handleAddClass,
    handleRemoveClass,
    handleSave,
  };
};
